import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { getStartingPosition, Select } from '@aline/common';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, IconButton, Skeleton } from '@mui/material';
import { CirclePlus, GripVertical, Trash2 } from 'lucide-react';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { makeStyles } from 'tss-react/mui';

import { SortableRuleRow } from './SortableRuleRow';
import type { PlaybookDetailCategory, PlaybookDetailRule } from './types';

const useStyles = makeStyles<void, 'dragHandleIcon' | 'headerButton'>()(
  (theme, _params, classes) => ({
    addRuleBottomButton: {
      '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: theme.spacing(1),
      },
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
      },
      borderRadius: 6,
      color: theme.appColors.primary,
      fontSize: 12,
      fontWeight: 500,
      justifyContent: 'flex-start',
      padding: theme.spacing(0.75, 1),
      textTransform: 'none',
      width: '100%',
    },
    addRuleRow: {
      alignItems: 'center',
      display: 'flex',
      // Sits the same distance below the last rule as the rules are from each
      // other, by cancelling most of the card's gap.
      marginTop: `calc(${theme.spacing(0.25)} - ${theme.spacing(1.5)})`,
    },
    categoryName: {
      flexGrow: 1,
      minWidth: 0,
    },
    categoryNameDisplay: {
      fontSize: 16,
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    categoryNameInput: {
      fontSize: 16,
      fontWeight: 600,
    },
    categoryNamePlaceholder: {
      color: theme.appColors.secondaryType,
    },
    /**
     * The handle for reordering categories. The span covers the whole page
     * gutter left of the card, so moving the cursor off the card towards the
     * grip still counts as hovering the card and the grip stays visible until
     * it is reached. The glyph is centered on the card; the strip is the
     * grab target.
     */
    dragHandle: {
      alignItems: 'center',
      bottom: 0,
      color: theme.appColors.secondaryType,
      cursor: 'grab',
      display: 'flex',
      justifyContent: 'center',
      left: -24,
      position: 'absolute',
      top: 0,
      touchAction: 'none',
      width: 24,
    },
    // Only the glyph fades, so the hit area above stays in place at rest.
    dragHandleIcon: {
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    // A card per category: the header, its rules and the add-rule row share one
    // border, matching the position cards in the details panel.
    group: {
      /**
       * Hovering anywhere in the card reveals the header's delete control, not
       * just hovering the header itself. Keyboard focus on the control reveals
       * it too.
       */
      [`&:has(.${classes.headerButton}:focus-visible) .${classes.headerButton}, &:hover .${classes.headerButton}, &:hover .${classes.dragHandleIcon}`]:
        {
          opacity: 1,
        },
      border: `1px solid ${theme.appColors.rule}`,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 2, 1, 2),
      position: 'relative',
    },
    // The card being dragged: it is the one that moves, so it is lifted above
    // the siblings it passes and its grip reads as held.
    groupDragging: {
      [`& .${classes.dragHandleIcon}`]: {
        opacity: 1,
      },
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[4],
      cursor: 'grabbing',
      zIndex: 1,
    },
    header: {
      alignItems: 'center',
      borderBottom: `1px solid ${theme.appColors.rule}`,
      display: 'flex',
      gap: theme.spacing(1),
      // Bleeds through the card's side padding so the divider runs edge to edge;
      // the padding comes back inside so the contents stay put.
      margin: theme.spacing(0, -2),
      padding: theme.spacing(0, 2, 1),
    },
    headerButton: {
      color: theme.appColors.secondaryType,
      // Revealed on card hover/focus so the resting header is just the name.
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    noRules: {
      border: '1px dashed transparent',
      borderRadius: 6,
      color: theme.appColors.secondaryType,
      fontSize: 14,
      fontStyle: 'italic',
      padding: theme.spacing(0.75, 1.5),
      textAlign: 'center',
      transition: 'border-color 120ms ease, color 120ms ease',
    },
    // An empty category is the drop target itself, having no rule to stand in
    // for it, so its placeholder lights up to say the drag can end here.
    noRulesDropTarget: {
      borderColor: theme.appColors.primary,
      color: theme.appColors.primary,
    },
    ruleSkeleton: {
      borderRadius: 6,
      height: 36,
    },
    rules: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.25),
    },
  })
);

interface RuleCategoryGroupProps {
  category: PlaybookDetailCategory;
  onAddRule: () => void;
  onDeleteCategory: () => void;
  onDeleteRule: (ruleId: UUID) => void;
  onRenameCategory: (newName: string) => void;
  onSelectRule: (ruleId: UUID) => void;
  /**
   * The rules to show under the category, in order. Passed separately from the
   * category because mid-drag they come from the drag's draft order rather
   * than from `category.rules`.
   */
  rules: readonly PlaybookDetailRule[];
  selectedRuleId: UUID | null;
  disabled?: boolean;
  /**
   * Open the name for editing whenever this changes to a non-zero value, for a
   * category that was just added (or re-requested via "Add category").
   */
  focusNameSeq?: number;
  /** One trailing rule-row skeleton while generation writes into this card. */
  isGeneratingOpen?: boolean;
}

/**
 * One category and the rules filed under it, rendered in the order given —
 * there is no client-side sort.
 *
 * The card is sortable among its siblings by the grip in the gutter, and is
 * also a drop target for rules, keyed by the category id, so a rule can be
 * dragged into it from another category even when it has no rules of its own
 * to drop beside. `useSortable` provides both: the droppable it registers is
 * the one the rule drag lands on.
 */
export function RuleCategoryGroup({
  category,
  disabled = false,
  focusNameSeq = 0,
  isGeneratingOpen = false,
  onAddRule,
  onDeleteCategory,
  onDeleteRule,
  onRenameCategory,
  onSelectRule,
  rules,
  selectedRuleId,
}: RuleCategoryGroupProps): JSX.Element {
  const { classes, cx } = useStyles();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // The rule whose delete is awaiting confirmation, if any.
  const [ruleToDelete, setRuleToDelete] = useState<PlaybookDetailRule | null>(null);
  const { isDragging, isOver, listeners, setNodeRef, transform, transition } = useSortable({
    disabled,
    id: category.id,
  });
  const ruleCountLabel = `${rules.length} ${rules.length === 1 ? 'rule' : 'rules'}`;

  // A blank rule has nothing to lose, so it goes without asking.
  function handleDeleteRuleClick(rule: PlaybookDetailRule): void {
    if (getIsBlankRule(rule)) onDeleteRule(rule.id);
    else setRuleToDelete(rule);
  }

  return (
    <div
      ref={setNodeRef}
      className={cx(classes.group, isDragging && classes.groupDragging)}
      data-cy={Select.playbooks.categoryCard}
      /**
       * Translate only: dnd-kit's scale is the ratio between the card's height
       * before and after the reorder, and cards holding different numbers of
       * rules would stretch or squash on drop.
       */
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      {/* Listeners only, not `attributes`: those would make the card a
          focusable role="button" around its real controls, and announce
          keyboard pick-up instructions that no keyboard sensor here honours. */}
      <span
        aria-hidden
        className={classes.dragHandle}
        data-cy={Select.playbooks.categoryDragHandle}
        {...listeners}
      >
        <GripVertical className={classes.dragHandleIcon} size={16} />
      </span>
      <div className={classes.header}>
        <div className={classes.categoryName} data-cy={Select.playbooks.categoryName}>
          <InlineEditField
            key={focusNameSeq}
            classes={{
              display: classes.categoryNameDisplay,
              input: classes.categoryNameInput,
              placeholder: classes.categoryNamePlaceholder,
            }}
            disabled={disabled}
            editTooltip="Rename category"
            initiallyEditing={focusNameSeq > 0}
            placeholder="Type a category title..."
            textPlaceholder="Untitled category"
            value={category.name}
            onSave={onRenameCategory}
          />
        </div>
        <AlineTooltip arrow placement="top" title="Delete category and its rules">
          <IconButton
            className={classes.headerButton}
            data-cy={Select.playbooks.categoryDelete}
            disabled={disabled}
            size="small"
            // An empty category has nothing to lose, so it goes without asking.
            onClick={() => (rules.length === 0 ? onDeleteCategory() : setDeleteConfirmOpen(true))}
          >
            <Trash2 size={14} />
          </IconButton>
        </AlineTooltip>
      </div>

      <SortableContext items={rules.map(rule => rule.id)} strategy={verticalListSortingStrategy}>
        <div className={classes.rules}>
          {rules.length === 0 && !isGeneratingOpen && (
            <div
              className={cx(classes.noRules, isOver && classes.noRulesDropTarget)}
              data-cy={Select.playbooks.categoryEmpty}
            >
              No rules in this category yet.
            </div>
          )}
          {rules.map(rule => (
            <SortableRuleRow
              key={rule.id}
              disabled={disabled}
              rule={rule}
              selected={rule.id === selectedRuleId}
              onDelete={() => handleDeleteRuleClick(rule)}
              onSelect={() => onSelectRule(rule.id)}
            />
          ))}
          {isGeneratingOpen && <Skeleton className={classes.ruleSkeleton} variant="rounded" />}
        </div>
      </SortableContext>

      <div className={classes.addRuleRow}>
        <Button
          className={classes.addRuleBottomButton}
          data-cy={Select.playbooks.addRule}
          disabled={disabled}
          size="small"
          startIcon={<CirclePlus size={16} />}
          onClick={onAddRule}
        >
          Add Rule
        </Button>
      </div>

      <ConfirmationDialog
        message={`Are you sure you want to delete "${category.name || 'Untitled category'}" and the ${ruleCountLabel} under it? This action cannot be undone.`}
        open={deleteConfirmOpen}
        primaryDataCy={Select.playbooks.categoryDeleteConfirm}
        title="Delete category?"
        onClickPrimary={() => {
          setDeleteConfirmOpen(false);
          onDeleteCategory();
        }}
        onClose={() => setDeleteConfirmOpen(false)}
      />
      <ConfirmationDialog
        message={`Are you sure you want to delete "${ruleToDelete ? getRuleDeleteLabel(ruleToDelete) : ''}"? This action cannot be undone.`}
        open={ruleToDelete !== null}
        primaryDataCy={Select.playbooks.ruleDeleteConfirm}
        title="Delete rule?"
        onClickPrimary={() => {
          if (ruleToDelete) onDeleteRule(ruleToDelete.id);
          setRuleToDelete(null);
        }}
        onClose={() => setRuleToDelete(null)}
      />
    </div>
  );
}

/**
 * Nobody has named it and nothing is typed into any position, so nothing is
 * lost by deleting it.
 */
function getIsBlankRule(rule: PlaybookDetailRule): boolean {
  if (rule.title.trim()) return false;

  return rule.positions.every(
    position => !position.ruleText.trim() && !(position.aiGuidance ?? '').trim()
  );
}

/** Same label the row shows: title, else the starting position, else Untitled. */
function getRuleDeleteLabel(rule: PlaybookDetailRule): string {
  const title = rule.title.trim();
  if (title) return title;
  if (rule.positions.length === 0) return 'Untitled Rule';
  return getStartingPosition(rule.positions).ruleText.trim() || 'Untitled Rule';
}
